import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Database, Download, Trash2, RotateCcw, Plus, Loader2, HardDrive, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

export default function BackupPage() {
  const { user } = useAuth();
  const [backups, setBackups] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);

  useEffect(() => { fetchBackups(); }, []);

  const fetchBackups = async () => {
    try {
      const [listRes, statusRes] = await Promise.all([
        api.get('/backup/list'),
        api.get('/backup/status').catch(() => ({ data: null })),
      ]);
      setBackups(listRes.data);
      setStatus(statusRes.data);
    } catch { toast.error('Failed to load backups'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.post('/backup/create');
      toast.success(res.data.message);
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backup failed');
    } finally { setCreating(false); }
  };

  const handleDownload = async (filename) => {
    try {
      const res = await api.get(`/backup/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete backup ${filename}?`)) return;
    try {
      await api.delete(`/backup/${filename}`);
      toast.success('Backup deleted');
      fetchBackups();
    } catch { toast.error('Failed to delete'); }
  };

  const handleRestore = async (filename) => {
    if (!window.confirm(`Restore database from ${filename}? A pre-restore backup will be created first.`)) return;
    setRestoring(filename);
    try {
      const res = await api.post(`/backup/restore/${filename}`);
      toast.success(res.data.message);
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally { setRestoring(null); }
  };

  if (!user?.is_super_admin) {
    return (
      <div className="p-6 text-center" data-testid="backup-forbidden">
        <Database className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h2 className="font-display text-xl font-bold">Admin Only</h2>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl lg:max-w-none mx-auto space-y-6" data-testid="backup-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-primary" />
            Backups
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Auto-backup daily at 2 AM IST. Keep last 7.</p>
        </div>
        <Button
          onClick={handleCreate}
          disabled={creating}
          data-testid="create-backup-btn"
          className="rounded-full"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {creating ? 'Backing up...' : 'Backup Now'}
        </Button>
      </div>

      {/* iter144 — daily backup status banner */}
      {status && (
        <Card
          className={`p-3 border-2 rounded-2xl ${
            status.backed_up_today
              ? 'border-emerald-500/40 bg-emerald-500/5'
              : (status.stale_hours || 0) > 36
                ? 'border-red-500/40 bg-red-500/5'
                : 'border-amber-500/40 bg-amber-500/5'
          }`}
          data-testid="backup-status-banner"
        >
          <div className="flex items-start gap-2">
            {status.backed_up_today ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0 space-y-1">
              <p className="text-sm font-bold">
                {status.backed_up_today
                  ? `✓ Backed up today (${status.today_ist} IST)`
                  : `⚠ No backup yet today (${status.today_ist} IST)`}
              </p>
              {status.latest_auto && (
                <p className="text-xs text-muted-foreground">
                  Last automatic backup: <span className="font-mono">{status.latest_auto.filename}</span> ·{' '}
                  {status.stale_hours != null
                    ? `${status.stale_hours}h ago`
                    : new Date(status.latest_auto.created_at).toLocaleString()}
                </p>
              )}
              {!status.latest_auto && (
                <p className="text-xs text-muted-foreground">
                  No automatic backup has ever run. Click <span className="font-bold">Backup Now</span> to create one manually.
                </p>
              )}
              {status.latest && status.latest_file_on_disk === false && (
                <p className="text-xs text-red-700 dark:text-red-300">
                  ⚠ The most recent backup file is <span className="font-bold">not on disk</span> — your container's <code className="font-mono">/app/backups</code> directory was wiped (this happens on every redeploy with Emergent's native deployment). Click <span className="font-bold">Download</span> on each backup as soon as it's created and store off-platform.
                </p>
              )}
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <p className="text-center text-muted-foreground py-8">Loading...</p>
      ) : backups.length === 0 ? (
        <Card className="p-8 text-center border-2 rounded-2xl" data-testid="no-backups">
          <HardDrive className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No backups yet. Create your first backup.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {backups.map((backup, i) => (
            <motion.div
              key={backup.filename}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <Card className="p-4 border-2 rounded-2xl" data-testid={`backup-${backup.filename}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Database className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-sm">{backup.filename}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(backup.created_at).toLocaleString()} — {backup.size_mb} MB
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handleDownload(backup.filename)}
                      data-testid={`download-${backup.filename}`}
                      className="rounded-full"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handleRestore(backup.filename)}
                      disabled={restoring === backup.filename}
                      data-testid={`restore-${backup.filename}`}
                      className="rounded-full text-blue-600"
                    >
                      {restoring === backup.filename ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => handleDelete(backup.filename)}
                      data-testid={`delete-backup-${backup.filename}`}
                      className="rounded-full text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
